import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CourseAssignmentDetail } from "@/services/studentReportingService";
import { reportExportUtils } from "@/utils/reportExportUtils";
import { Award, BookOpen, Clock, TrendingUp } from "lucide-react";

interface IndividualStudentTableProps {
  data: CourseAssignmentDetail[];
}

export const IndividualStudentTable = ({ data }: IndividualStudentTableProps) => {
  if (data.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        No course assignments found for this student
      </div>
    );
  }

  const studentName = data[0].studentName;
  const studentEmail = data[0].studentEmail;

  // Calculate summary stats
  const totalCourses = data.length;
  const completedCourses = data.filter((d) => d.status === 'completed').length;
  const inProgressCourses = data.filter((d) => d.status === 'in_progress').length;
  const certificatesEarned = data.filter((d) => d.hasCertificate).length;
  const totalTimeSpent = data.reduce((sum, d) => sum + d.timeSpent, 0);
  const averageGrade =
    data.filter((d) => d.finalGrade !== null).length > 0
      ? data.reduce((sum, d) => sum + (d.finalGrade || 0), 0) /
        data.filter((d) => d.finalGrade !== null).length
      : null;

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-success">Completed</Badge>;
      case 'in_progress':
        return <Badge className="bg-warning">In Progress</Badge>;
      case 'active':
        return <Badge variant="outline">Active</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      {/* Student Header Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">{studentName}</CardTitle>
          <p className="text-muted-foreground">{studentEmail}</p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="flex items-center gap-3">
              <BookOpen className="h-8 w-8 text-primary" />
              <div>
                <p className="text-2xl font-bold">{totalCourses}</p>
                <p className="text-xs text-muted-foreground">Total Courses</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <TrendingUp className="h-8 w-8 text-success" />
              <div>
                <p className="text-2xl font-bold">{completedCourses}</p>
                <p className="text-xs text-muted-foreground">Completed</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Clock className="h-8 w-8 text-warning" />
              <div>
                <p className="text-2xl font-bold">{inProgressCourses}</p>
                <p className="text-xs text-muted-foreground">In Progress</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Award className="h-8 w-8 text-accent" />
              <div>
                <p className="text-2xl font-bold">{certificatesEarned}</p>
                <p className="text-xs text-muted-foreground">Certificates</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">
                {averageGrade ? Math.round(averageGrade) : '-'}
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {reportExportUtils.formatPercentage(averageGrade)}
                </p>
                <p className="text-xs text-muted-foreground">Avg Grade</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Course Assignments Table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Course Title</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Level</TableHead>
              <TableHead>Assigned Date</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Completion Date</TableHead>
              <TableHead className="text-right">Grade</TableHead>
              <TableHead className="text-right">Time Spent</TableHead>
              <TableHead className="text-center">Certificate</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((assignment) => (
              <TableRow key={assignment.id}>
                <TableCell className="font-medium">{assignment.courseTitle}</TableCell>
                <TableCell>
                  <Badge variant="outline">{assignment.category}</Badge>
                </TableCell>
                <TableCell className="capitalize">{assignment.level}</TableCell>
                <TableCell>{reportExportUtils.formatDate(assignment.assignedDate)}</TableCell>
                <TableCell>{getStatusBadge(assignment.status)}</TableCell>
                <TableCell>{reportExportUtils.formatDate(assignment.completionDate)}</TableCell>
                <TableCell className="text-right">
                  {reportExportUtils.formatPercentage(assignment.finalGrade)}
                </TableCell>
                <TableCell className="text-right">
                  {reportExportUtils.formatDuration(assignment.timeSpent)}
                </TableCell>
                <TableCell className="text-center">
                  {assignment.hasCertificate ? (
                    <Award className="h-5 w-5 text-success mx-auto" />
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
